import Image from 'next/image';
import Link from 'next/link';

import { Text } from '@/components/atomic/atoms';
import { staticMediaStoreBaseURL } from '@/config/common';
import {
  companySectionData,
  policySectionData,
  socialMediaIconList,
  STATS
} from '@/config/landingPage';
import getInitiateWhatsappChatLinkForSARA from '@/services/landingPage.service';

const Heading = ({ label }) => (
  <div>
    <Text
      {...{
        content: label,
        className:
          'text-xs font-medium sm:text-base md:text-lg uppercase text-white mb-1'
      }}
    />
    <div className='w-11 h-0.5 bg-white'></div>
  </div>
);

const LinkSection = ({ label, href, target = '_self' }) => (
  <li>
    <Link
      href={href}
      target={target}
    >
      <Text
        {...{
          content: label,
          className:
            'text-white text-xs md:text-base sm:text-sm font-light py-1'
        }}
      />
    </Link>
  </li>
);

const FooterMenuSection = ({ className = 'w-2/4', data, headingLabel }) => (
  <div className={`${className} md:w-1/4 pt-4 md:pt-0`}>
    <Heading {...{ label: headingLabel }} />
    <ul className='flex pt-2 gap-4 w-max'>
      {data.map(({ label, href, target }, index) => (
        <LinkSection
          key={index}
          {...{ label, href, target }}
        />
      ))}
    </ul>
  </div>
);

const LandingPageFooter = () => {
  return (
    <footer className='w-full bg-black text-white'>
      <div className='w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16 text-center max-w-4xl mx-auto'>
        <div
          className={`text-[1.5rem] xs:text-[1.75rem] sm:text-[1.75rem] md:text-[3rem] lg:text-[4.5rem] xl:text-[4.5rem] font-black leading-[1.25] text-stroke-normal xs:text-stroke-normal sm:text-stroke-normal md:text-stroke-bold tracking-wide`}
        >
          Ready to Transform <br className='block md:hidden' />
          <Text
            {...{
              className: 'bg-brand-gradient bg-clip-text text-transparent',
              content: 'Your Event Business?',
              HtmlTag: 'span'
            }}
          />
        </div>
        <Text
          {...{
            className:
              'text-xs xs:text-sm sm:text-sm md:text-lg lg:text-lg mt-3 xs:mt-4 sm:mt-4 mx-auto',
            content: `Join the UAE's leading AI-powered event ecosystem. Gain more customers, streamline your operations, and effortlessly boost your earnings.`
          }}
        />
        <Link
          className={`mt-6 xs:mt-8 sm:mt-6 inline-flex items-center justify-center gap-1 xs:gap-1.5 sm:gap-1 md:gap-2 rounded-lg px-3 xs:px-4 sm:px-3 md:px-6 py-2.5 xs:py-2.5 sm:py-3 md:py-4 text-xs xs:text-sm sm:text-sm md:text-base font-semibold shadow-lg bg-brand-gradient text-white hover:opacity-90 transition-opacity w-full xs:w-auto sm:w-auto`}
          href={getInitiateWhatsappChatLinkForSARA()}
          rel='noopener noreferrer nofollow'
          target='_blank'
        >
          <Image
            alt='WhatsApp'
            className='w-4 h-4 sm:w-5 sm:h-5'
            height={20}
            src={`${staticMediaStoreBaseURL}/icons/whatsapp-green.svg`}
            width={20}
          />
          Meet Sara - Your Onboarding Expert ✨
        </Link>
      </div>
      <div className='px-3 xs:px-4 md:py-6 pb-10 xs:pb-12'>
        <div className='max-w-4xl mx-auto flex flex-col md:flex-row sm:flex-col justify-between text-center md:gap-4 gap-4 sm:gap-6'>
          {STATS.map(({ label, value }, index) => (
            <div
              className='flex-1'
              key={index}
            >
              <Text
                {...{
                  className:
                    'bg-brand-gradient bg-clip-text text-transparent text-xl xs:text-2xl font-extrabold',
                  content: value
                }}
              />
              <Text
                {...{
                  className: 'text-base',
                  content: label
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <hr className='border-t border-white/20 w-full max-w-7xl mx-auto' />
      <div className='px-4 sm:px-6 lg:px-8 py-10'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col gap-2 sm:gap-3'>
            <Link
              className='flex items-center gap-2 hover:opacity-90 transition-opacity'
              href='/'
            >
              <div className='relative w-[85px] sm:w-[75px] md:w-[113px]'>
                <Image
                  alt='Hafla Logo'
                  className='w-full h-auto aspect-[113/36]'
                  height={0}
                  priority
                  src={`${staticMediaStoreBaseURL}/icons/hafla-transparent-icon.svg`}
                  width={0}
                />
              </div>
            </Link>
            <Text
              {...{
                className: 'text-xl sm:text-base md:text-xl lg:text-xl',
                content: 'Events Simplified'
              }}
            />
          </div>
        </div>

        <div className='max-w-7xl mx-auto mt-2 md:mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 md:gap-8'>
          <div className='hidden md:flex flex-col justify-between'>
            <div className='flex gap-4'>
              {socialMediaIconList.map(({ url, icon, alt }, index) => (
                <Link
                  href={url}
                  key={index}
                >
                  <Image
                    alt={alt}
                    className='w-5 h-5'
                    height={0}
                    src={`${staticMediaStoreBaseURL}/icons/${icon}`}
                    style={{ width: 30, height: 30 }}
                    width={0}
                  />
                </Link>
              ))}
            </div>
            <Text
              {...{
                className: 'text-sm text-white/50 mt-6 md:mt-8 lg:mt-auto',
                content: '© 2025 Hafla. All rights reserved.'
              }}
            />
          </div>
          <FooterMenuSection
            {...{
              className: 'w-full',
              data: companySectionData,
              headingLabel: 'COMPANY'
            }}
          />
          <FooterMenuSection
            {...{
              className: 'w-full',
              data: policySectionData,
              headingLabel: 'POLICY'
            }}
          />
          <div className='flex flex-col items-center sm:items-start md:hidden mt-8'>
            <div className='flex gap-4'>
              {socialMediaIconList.map(({ alt, icon, url }, index) => (
                <Link
                  href={url}
                  key={index}
                >
                  <Image
                    alt={alt}
                    className='w-5 h-5'
                    height={0}
                    src={`${staticMediaStoreBaseURL}/icons/${icon}`}
                    style={{ width: 30, height: 30 }}
                    width={0}
                  />
                </Link>
              ))}
            </div>
            <Text
              {...{
                className:
                  'text-sm text-white/50 mt-6 text-center sm:text-left',
                content: '© 2025 Hafla. All rights reserved.'
              }}
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LandingPageFooter;
